import React, { memo, useCallback, useEffect } from "react";
import propTypes from "prop-types";
import { Transition } from "react-transition-group";
import cx from "classnames";
import styles from "./Content.module.scss";
import { useExpansionPanelCtx } from "../ExpansionPanelCtx";
import useNode from "../../../hooks/useNode";
import triggerBrowserReflow from "../../../utils/triggerBrowserReflow";

const animationStyles = node => ({
  entering: {
    height: 0
  },
  entered: {
    height: node.offsetHeight
  },
  exiting: {
    height: 0
  },
  exited: {
    height: 0
  }
});

const Content = ({ children, className }) => {
  const { expanded, mountOnEnter, unmountOnExit, onChange } = useExpansionPanelCtx();
  const [contentNode, contentRef] = useNode();
  const [containerNode, containerRef] = useNode();

  const onTransitionEnd = useCallback(
    ({ target }) => {
      if (target.offsetHeight > 0) {
        target.style.height = "auto"; // eslint-disable-line no-param-reassign
        triggerBrowserReflow(target);
        onChange("entered", expanded);
        return;
      }

      onChange("exited", expanded);
    },
    [onChange, expanded]
  );

  useEffect(() => {
    if (!containerNode.addEventListener) {
      return () => {};
    }
    containerNode.addEventListener("transitionend", onTransitionEnd);

    return () => {
      containerNode.removeEventListener("transitionend", onTransitionEnd);
    };
  }, [onTransitionEnd, containerNode]);

  const handleExit = useCallback(
    node => {
      node.style.height = `${contentNode.offsetHeight}px`; // eslint-disable-line no-param-reassign
      onChange("exit", expanded);
    },
    [onChange, expanded, contentNode.offsetHeight]
  );

  const onEnter = useCallback(() => onChange("enter", expanded), [onChange, expanded]);

  return (
    <Transition
      appear
      onExit={handleExit}
      onEnter={onEnter}
      in={expanded}
      timeout={{ exit: 200, enter: 0 }}
      mountOnEnter={mountOnEnter}
      unmountOnExit={unmountOnExit}
    >
      {state => (
        <div
          ref={containerRef}
          style={{
            ...animationStyles(contentNode)[state]
          }}
          className={cx(styles.contentContainer, className)}
        >
          <div ref={contentRef} className={cx(styles.content, { expanded })}>
            {children}
          </div>
        </div>
      )}
    </Transition>
  );
};

Content.defaultProps = {
  className: undefined
};

Content.propTypes = {
  children: propTypes.node.isRequired,
  className: propTypes.string
};
export default memo(Content);
